import { useApplicationDetail } from '@axo/shared/data-access/hooks';
import { DebtTypeTag } from './Cells/DebtTypeTag';
import styles from './DebtRegistryTable.module.css';
import { Button, Checkbox, Tag } from '@blueprintjs/core';
import { useContext, useMemo, useState } from 'react';
import { debt_registry, loan_application } from '@axo/shared/data-access/types';
import { RefinanceCalculator } from './RefinanceCalculator';
import { ApplicationContext } from '../../../store/context';
import { getLocaleAndCountry } from '@axo/shared/util/string';
import { roundUpToStep } from '../../../pages/Search/CreateNewApplicationTab/amountConfig';
import { calculateAnnuity } from './calculateAnnuity';
import { useApplicationDataStore } from '../../../store/applicationDataStore';
import { useFormContext } from 'react-hook-form';
import { ApplicationDataFormValues } from '../../ApplicationData/ApplicationDataForm/ApplicationDataFormSchema';
import { Product } from '@axo/shared/data-access/types/loan_quote';
import { ConditionalTooltip } from '../../ConditionalTooltip/ConditionalTooltip';

type DebtRegistryCalculator = Record<string, DebtRegistryCalculatorValue>;

interface DebtRegistryCalculatorValue {
  balance: number;
  interestBearingBalance: number;
  nonInterestBearingBalance: number;
}

export interface DebtRegistryTableProps {
  debts: debt_registry.DebtRegistryDebtEntry[];
  applicationID: string;
  personID: string;
}

interface DebtRegistryPresentation extends debt_registry.DebtRegistryDebtEntry {
  annuity: number | null;
}

export const DebtRegistryTable = ({
  debts,
  applicationID,
  personID,
}: DebtRegistryTableProps) => {
  const loanApplication = useApplicationDetail(applicationID);
  const { state } = useContext(ApplicationContext);
  const [calculator, setCalculator] = useState<DebtRegistryCalculator>({});
  const [cashAmount, setCashAmount] = useState<number>(0);
  const { isEditing } = useApplicationDataStore(applicationID);
  const { setValue, getValues } = useFormContext<ApplicationDataFormValues>();

  const { Debts: formDebts } = getValues();

  function roundToDecimals(value: number, decimals?: number) {
    // The factor is based on the number of decimals
    const factor = Math.pow(10, decimals ?? 0);
    // Adding a small number (EPSILON) to handle floating point precision issues
    return Math.round((value + Number.EPSILON) * factor) / factor;
  }

  const transformedDebts = useMemo(() => {
    function divideBy100(value: number | null, decimals?: number) {
      if (!value) return value;
      return roundToDecimals(value / 100, decimals);
    }
    function mapCoBorrower(value: string | null) {
      if (value === '0') return 'No co-borrower';
      if (value === '1') return 'Is co-borrower';
      if (value === '2') return 'Has co-borrower';
      return '-';
    }
    return debts.map((debt) => {
      return {
        ...debt,
        creditLimit: divideBy100(debt.creditLimit),
        interestBearingBalance: divideBy100(debt.interestBearingBalance),
        nonInterestBearingBalance: divideBy100(debt.nonInterestBearingBalance),
        nominalInterestRate: divideBy100(debt.nominalInterestRate, 2),
        installmentCharges: divideBy100(debt.installmentCharges),
        originalBalance: divideBy100(debt.originalBalance),
        balance: divideBy100(debt.balance),
        annuity: roundToDecimals(
          calculateAnnuity(
            (debt.balance ?? 0) / 100,
            (debt.nominalInterestRate ?? 0) / 100,
            (debt.installmentCharges ?? 0) / 100,
            debt.terms ? parseInt(debt.terms) : null,
          ) ?? 0,
        ),
        coBorrower: mapCoBorrower(debt.coBorrower),
      } satisfies DebtRegistryPresentation;
    });
  }, [debts]);

  const calculatorBalance = Object.values(calculator).reduce(
    (acc, value) => (value.balance ? acc + value.balance : acc),
    0,
  );
  const calculatorInterestBearingBalance = Object.values(calculator).reduce(
    (acc, value) =>
      value.interestBearingBalance ? acc + value.interestBearingBalance : acc,
    0,
  );
  const calculatorNonInterestBearingBalance = Object.values(calculator).reduce(
    (acc, value) =>
      value.nonInterestBearingBalance
        ? acc + value.nonInterestBearingBalance
        : acc,
    0,
  );

  const getWeight = (debt: debt_registry.DebtRegistryDebtEntry) => {
    return [
      debt.balance ?? 0,
      debt.interestBearingBalance ?? 0,
      debt.nonInterestBearingBalance ?? 0,
    ].reduce((acc, value) => {
      return acc + value;
    }, 0);
  };

  const getWeightedAverage = (debts: debt_registry.DebtRegistryDebtEntry[]) => {
    let totalWeight = 0;
    const interestTimesWeight = debts.reduce((acc, debt) => {
      const weight = getWeight(debt);
      const total = (debt.nominalInterestRate ?? 0) * weight;
      totalWeight += weight;
      return acc + total;
    }, 0);

    if (totalWeight === 0) return 0;
    return interestTimesWeight / totalWeight;
  };

  const calculatedInterestRate = () => {
    const debts = Object.entries(calculator)
      .map(([debtId, value]) => {
        const debt = transformedDebts.find((debt) => debt.id === debtId);
        if (!debt) return null;
        return {
          ...debt,
          balance: value.balance !== 0 ? value.balance : debt.balance,
          interestBearingBalance:
            value.interestBearingBalance !== 0
              ? value.interestBearingBalance
              : debt.interestBearingBalance,
          nonInterestBearingBalance:
            value.nonInterestBearingBalance !== 0
              ? value.nonInterestBearingBalance
              : debt.nonInterestBearingBalance,
        };
      })
      .filter((debt) => debt !== null) as debt_registry.DebtRegistryDebtEntry[];
    return getWeightedAverage(debts);
  };

  const totalDebtNotRounded =
    calculatorBalance +
    calculatorInterestBearingBalance +
    calculatorNonInterestBearingBalance;

  const totalDebt = roundUpToStep(
    state.marketCountry ?? '',
    totalDebtNotRounded,
  );

  const totalAmount = roundUpToStep(
    state.marketCountry ?? '',
    totalDebtNotRounded + cashAmount,
  );

  const handleCalculatorValueChange = (
    debt: debt_registry.DebtRegistryDebtEntry,
    type: 'balance' | 'interestBearingBalance' | 'nonInterestBearingBalance',
  ) => {
    setCalculator((prev) => {
      const debtAmount = debt[type] ?? 0;
      // Remove the debt if the type is already checked
      if (prev[debt.id] && prev[debt.id][type] > 0) {
        const newState = Object.fromEntries(
          Object.entries(prev).filter(([id]) => id !== debt.id),
        );
        return newState;
      } else if (debtAmount > 0) {
        return {
          ...prev,
          [debt.id]: {
            ...prev[debt.id],
            [type]: debt[type] ?? 0,
          },
        };
      }
      return prev;
    });
  };

  const isTotalChecked = (
    type: 'balance' | 'interestBearingBalance' | 'nonInterestBearingBalance',
  ) => {
    if (!transformedDebts) return false;
    if (Object.entries(calculator).length === 0) return false;
    if (transformedDebts.every((debt) => debt[type] === 0)) return false;
    return transformedDebts.every(
      (debt) => calculator[debt.id]?.[type] > 0 || debt[type] === 0,
    );
  };

  const handleCalculatorTotalValueChange = (
    type: 'balance' | 'interestBearingBalance' | 'nonInterestBearingBalance',
  ) => {
    const checked = isTotalChecked(type);
    if (transformedDebts.length > 0) {
      const newCalculator = transformedDebts.reduce((acc, debt) => {
        const debtAmount = debt[type] ?? 0;
        if (checked) {
          // Remove the debt if the type is already checked
          const { [debt.id]: _, ...rest } = acc;
          return rest;
        } else if (debtAmount > 0) {
          return {
            ...acc,
            [debt.id]: {
              ...acc[debt.id],
              [type]: debtAmount,
            },
          };
        }
        return acc;
      }, calculator);

      setCalculator(newCalculator);
    }
  };

  function formatNumber(value: number | null | undefined) {
    if (!value) return '-';
    return value.toLocaleString(getLocaleAndCountry(state.marketCountry || ''));
  }

  const totalCreditLimit =
    transformedDebts
      .map((debt) => debt.creditLimit ?? 0)
      .reduce((sum, a) => sum + a, 0) ?? 0;
  const totalBalance =
    transformedDebts
      .map((debt) => debt.balance ?? 0)
      .reduce((sum, a) => sum + a, 0) ?? 0;
  const totalInterestBearing =
    transformedDebts
      .map((debt) => debt.interestBearingBalance ?? 0)
      .reduce((sum, a) => sum + a, 0) ?? 0;
  const totalNonInterestBearing =
    transformedDebts
      .map((debt) => debt.nonInterestBearingBalance ?? 0)
      .reduce((sum, a) => sum + a, 0) ?? 0;
  const totalInstallmentCharges =
    transformedDebts
      .map((debt) => debt.installmentCharges ?? 0)
      .reduce((sum, a) => sum + a, 0) ?? 0;
  const totalInterestRate = getWeightedAverage(transformedDebts);

  const refinancedOfTotal = `${formatNumber(totalAmount.number)}/${formatNumber(
    totalDebt.number,
  )} ${loanApplication.data?.Currency}`;

  function handleUpdateLoanRequestWithAmountAndRefinance() {
    if (isEditing) {
      const selectedDebts = [];

      for (const key in calculator) {
        const debtValue = calculator[key];
        for (const prop in debtValue) {
          selectedDebts.push({
            [key]: {
              balance: debtValue[prop as keyof DebtRegistryCalculatorValue],
            },
          });
        }
      }

      const editingDebts = selectedDebts.map((selectedDebt) => {
        const debtId = Object.keys(selectedDebt)[0];
        const debt = debts.find((item) => item.id === debtId);
        return {
          ...debt,
          ...selectedDebt[debtId],
        };
      });

      const transformedEditingDebts = editingDebts.map((debt) => {
        return {
          Type: loan_application.DebtType.Personal,
          ID: null,
          Creditor: debt.financialInstitutionName || '',
          Debtor: loan_application.Debtor.MainApplicant,
          MonthlyDebtPayment: 0,
          NominalInterestRate: debt.nominalInterestRate || 0,
          OutstandingAmount: debt.balance || 0,
          RefinanceAmount: debt.balance || 0,
          Source: null,
        };
      });

      const transformedNonUsingDebts = formDebts
        .map((debt) => {
          return {
            ...debt,
            RefinanceAmount: 0,
          };
        })
        .filter(
          (formDebt) =>
            !transformedEditingDebts.some(
              (editingDebt) =>
                formDebt.OutstandingAmount === editingDebt.OutstandingAmount &&
                formDebt.Creditor === editingDebt.Creditor,
            ),
        );

      setValue('Debts', [
        ...transformedEditingDebts,
        ...transformedNonUsingDebts,
      ]);

      setValue('AppliedAmount', totalAmount.number);

      const applicationDataSection = document.querySelector(
        `[id="AceCard.ApplicationData.${applicationID}"]`,
      );
      if (applicationDataSection) {
        applicationDataSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }

  return (
    <>
      <table className={styles.debtTable}>
        <thead>
          <tr className={styles.header}>
            <th>Bank</th>
            <th align={'left'}>Debt type</th>
            <th>Credit limit</th>
            <th>Remaining amount</th>
            <th>Remaining terms</th>
            <th>Interest bearing</th>
            <th>Non Interest bearing</th>
            <th>Nom. interest rate</th>
            <th>Installment charges</th>
            <th>Monthly payment (annuity)</th>
            <th>Co-borrower</th>
          </tr>
        </thead>
        <tbody>
          {transformedDebts.map((debt) => (
            <tr key={debt.id}>
              <td className={styles.leftAlign}>
                {debt.financialInstitutionName}
              </td>
              <td className={styles.leftAlign}>
                <DebtTypeTag debtType={debt.type} />
              </td>
              <td>{formatNumber(debt.creditLimit)}</td>
              <td>
                <div className={styles.inline}>
                  <Checkbox
                    label={formatNumber(debt.balance)}
                    alignIndicator={'right'}
                    className={styles.checkbox}
                    onChange={() =>
                      handleCalculatorValueChange(debt, 'balance')
                    }
                    checked={!!calculator[debt.id]?.balance}
                  />
                </div>
              </td>
              <td>{debt.terms ? `${debt.terms} months` : '-'}</td>
              <td>
                <div className={styles.inline}>
                  <Checkbox
                    label={formatNumber(debt.interestBearingBalance)}
                    alignIndicator={'right'}
                    className={styles.checkbox}
                    onChange={() =>
                      handleCalculatorValueChange(
                        debt,
                        'interestBearingBalance',
                      )
                    }
                    checked={!!calculator[debt.id]?.interestBearingBalance}
                  />
                </div>
              </td>
              <td>
                <div className={styles.inline}>
                  <Checkbox
                    label={formatNumber(debt.nonInterestBearingBalance)}
                    alignIndicator={'right'}
                    className={styles.checkbox}
                    onChange={() =>
                      handleCalculatorValueChange(
                        debt,
                        'nonInterestBearingBalance',
                      )
                    }
                    checked={!!calculator[debt.id]?.nonInterestBearingBalance}
                  />
                </div>
              </td>
              <td>{debt.nominalInterestRate}</td>
              <td>{formatNumber(debt.installmentCharges)}</td>
              <td>{formatNumber(debt.annuity)}</td>
              <td>{debt.coBorrower}</td>
            </tr>
          ))}
          <tr>
            <td>
              <b>Total</b>
            </td>
            <td>
              <Tag minimal intent={'none'} round icon={'globe'}>
                All
              </Tag>
            </td>
            <td>{formatNumber(totalCreditLimit)}</td>
            <td>
              <div className={styles.inline}>
                <Checkbox
                  label={formatNumber(totalBalance)}
                  alignIndicator={'right'}
                  className={styles.checkbox}
                  onChange={() => handleCalculatorTotalValueChange('balance')}
                  checked={isTotalChecked('balance')}
                  disabled={totalBalance === 0}
                />
              </div>
            </td>
            <td>-</td>
            <td>
              <div className={styles.inline}>
                <Checkbox
                  label={formatNumber(totalInterestBearing)}
                  alignIndicator={'right'}
                  className={styles.checkbox}
                  onChange={() =>
                    handleCalculatorTotalValueChange('interestBearingBalance')
                  }
                  checked={isTotalChecked('interestBearingBalance')}
                />
              </div>
            </td>
            <td>
              <div className={styles.inline}>
                <Checkbox
                  label={formatNumber(totalNonInterestBearing)}
                  alignIndicator={'right'}
                  className={styles.checkbox}
                  onChange={() =>
                    handleCalculatorTotalValueChange(
                      'nonInterestBearingBalance',
                    )
                  }
                  checked={isTotalChecked('nonInterestBearingBalance')}
                />
              </div>
            </td>
            <td>{roundToDecimals(totalInterestRate, 1)}</td>
            <td>{formatNumber(totalInstallmentCharges)}</td>
            <td>{'-'}</td>
            <td>{'-'}</td>
          </tr>
        </tbody>
      </table>
      {!loanApplication.isLoading && (
        <>
          <RefinanceCalculator
            remainingAmount={calculatorBalance}
            nonInterestBearing={calculatorNonInterestBearingBalance}
            interestBearing={calculatorInterestBearingBalance}
            cashAmount={cashAmount}
            totalAmount={totalAmount}
            setCashAmount={setCashAmount}
            numberOfRemainingAmountSelected={
              Object.values(calculator).filter((value) => value.balance > 0)
                .length
            }
            numberOfInterestBearingSelected={
              Object.values(calculator).filter(
                (value) => value.interestBearingBalance > 0,
              ).length
            }
            numberOfNonInterestBearingSelected={
              Object.values(calculator).filter(
                (value) => value.nonInterestBearingBalance > 0,
              ).length
            }
            totalInterestRate={roundToDecimals(calculatedInterestRate(), 1)}
            currency={loanApplication.data?.Currency ?? undefined}
          />
          <div className={styles.actionBar}>
            <ConditionalTooltip
              conditions={[
                {
                  isTooltipVisible:
                    loanApplication.data?.Product === Product.SecuredLoan,
                  tooltipContent:
                    'This feature is not available in secured loans',
                  isChildDisabled: true,
                },
                {
                  isTooltipVisible: !isEditing,
                  tooltipContent: 'Must be on edit mode to update loan amount.',
                  isChildDisabled: true,
                },
                {
                  isTooltipVisible: personID !== loanApplication.data?.PersonID,
                  tooltipContent:
                    'Only main applicant is supported at the moment',
                  isChildDisabled: true,
                },
              ]}
              render={(disabled) => (
                <Button
                  disabled={disabled}
                  intent={'primary'}
                  icon={'send-to-graph'}
                  onClick={handleUpdateLoanRequestWithAmountAndRefinance}
                >
                  Update loan amount to: <b>{refinancedOfTotal}</b>
                </Button>
              )}
            />
          </div>
        </>
      )}
    </>
  );
};
